
import React, { useState, useEffect } from 'react';
import { AdService, _adWebHooks } from '../AdService';
import { Capacitor } from '@capacitor/core';

const GlobalAdModals: React.FC = () => {
    const [interstitialOpen, setInterstitialOpen] = useState(false);
    const [rewardedOpen, setRewardedOpen] = useState(false);
    const [rewardCallback, setRewardCallback] = useState<(() => void) | null>(null);
    const [countdown, setCountdown] = useState(5);
    const [isNative, setIsNative] = useState(false);

    useEffect(() => {
        setIsNative(Capacitor.isNativePlatform());
        
        // Register listeners with AdService for web placeholders
        _adWebHooks.onShowInterstitial = () => {
            setInterstitialOpen(true);
            setCountdown(5);
        };
        
        _adWebHooks.onShowRewarded = (callback: () => void) => {
            setRewardCallback(() => callback);
            setRewardedOpen(true);
            setCountdown(15);
        };

        return () => {
            _adWebHooks.onShowInterstitial = null;
            _adWebHooks.onShowRewarded = null;
        };
    }, []);

    useEffect(() => {
        let timer: any;
        if ((interstitialOpen || rewardedOpen) && countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        } else if (countdown === 0 && rewardedOpen && rewardCallback) {
            // Auto-complete reward when countdown hits 0
            rewardCallback();
            setRewardCallback(null);
        }
        return () => clearInterval(timer);
    }, [interstitialOpen, rewardedOpen, countdown, rewardCallback]);

    if (isNative) return null;
    if (!interstitialOpen && !rewardedOpen) return null;

    const handleClose = () => {
        if (countdown > 0) return;
        setInterstitialOpen(false);
        setRewardedOpen(false);
    };

    return (
        <div className="fixed inset-0 bg-black/95 z-[9999] flex flex-col items-center justify-center p-6 text-white animate-fade-in">
            <div className="absolute top-6 right-6 flex items-center gap-3 bg-black/40 px-4 py-2 rounded-full border border-white/10">
                <span className="text-xl font-mono font-bold w-6 text-center">{countdown > 0 ? countdown : ''}</span>
                <button 
                    onClick={handleClose}
                    disabled={countdown > 0}
                    className={`text-3xl leading-none ${countdown > 0 ? 'text-white/20' : 'text-white hover:text-pink-400 transition-colors'}`}
                >
                    <i className="ph-bold ph-x"></i>
                </button>
            </div>

            <div className="w-full max-w-md space-y-8 text-center">
                <div className="space-y-2">
                    <div className="inline-block px-3 py-1 bg-white/10 rounded text-[10px] font-bold uppercase tracking-widest text-white/60 border border-white/5">
                        Sponsored Advertisement
                    </div>
                    <h2 className="text-3xl font-black tracking-tight">
                        {rewardedOpen ? 'WATCH TO EARN REWARD' : 'INTERSTITIAL AD'}
                    </h2>
                </div>

                <div className="aspect-video bg-gradient-to-br from-purple-900/50 to-indigo-900/50 rounded-3xl border-2 border-white/10 flex flex-col items-center justify-center relative overflow-hidden group shadow-2xl">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
                    <i className="ph-duotone ph-play-circle text-8xl text-white/20 group-hover:text-white/40 transition-all duration-500 transform group-hover:scale-110"></i>
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 text-left bg-black/40 p-3 rounded-2xl backdrop-blur-md border border-white/10">
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                            <i className="ph-fill ph-game-controller text-2xl text-white"></i>
                        </div>
                        <div className="flex-grow">
                            <div className="font-bold text-sm">Puzzle Quest: Aurora</div>
                            <div className="text-[10px] opacity-60">The #1 Puzzle Game of 2026</div>
                        </div>
                        <button className="bg-white text-black text-[10px] font-black px-4 py-2 rounded-lg shadow-lg hover:bg-pink-400 hover:text-white transition-all">
                            INSTALL
                        </button>
                    </div>
                </div>

                <div className="space-y-4">
                    <p className="text-sm text-white/60 font-medium">
                        {rewardedOpen 
                            ? `Your reward will be granted in ${countdown} seconds...` 
                            : countdown > 0 
                                ? `You can skip this ad in ${countdown} seconds...` 
                                : 'You can now close this advertisement.'
                        }
                    </p>
                    {countdown === 0 && (
                        <button 
                            onClick={handleClose}
                            className="bg-white text-black font-black px-8 py-3 rounded-2xl shadow-xl hover:bg-pink-500 hover:text-white transition-all transform hover:scale-105 active:scale-95"
                        >
                            CONTINUE TO APP
                        </button>
                    )}
                </div>
            </div>

            <div className="absolute bottom-8 flex items-center gap-2 text-[10px] font-bold text-white/20 uppercase tracking-[0.2em]">
                <i className="ph-fill ph-shield-check"></i>
                AdMob Verified Partner
            </div>
        </div>
    );
};

export default GlobalAdModals;
